import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  ListTodo,
  Calendar,
  FileText,
  Users,
  Settings,
  BarChart,
  Trophy,
  LogOut,
  ChevronLeft,
  UserCog,
  Briefcase,
  Target,
  DollarSign,
  BookUser,
  ShoppingBag,
  CalendarDays,
  UserPlus,
  Building,
  BarChart3,
  CreditCard,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";

interface NavigationItem {
  title: string;
  href: string;
  icon: JSX.Element;
  allowedRoles: string[];
  requiresPosition?: string; // Optional position requirement
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      // Explizit zur Login-Seite weiterleiten nach erfolgreichem Logout
      navigate("/login");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Abmeldefehler",
        description: "Abmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.",
      });
    }
  };

  const navigationItems: NavigationItem[] = [
    {
      title: "Dashboard",
      href: "/dashboard",
      icon: <LayoutDashboard size={20} />,
      allowedRoles: ["admin", "co-admin", "user", "developer"],
    },
    {
      title: "Lead Liste",
      href: "/leads",
      icon: <ListTodo size={20} />,
      allowedRoles: ["admin", "co-admin", "user", "developer"],
    },
    {
      title: "Kalender",
      href: "/calendar",
      icon: <Calendar size={20} />,
      allowedRoles: ["admin", "co-admin", "user", "developer"],
    },
    {
      title: "Lead - Börse",
      href: "/lead-market",
      icon: <BarChart size={20} />,
      allowedRoles: ["admin", "user", "developer"],
    },
    {
      title: "Challenges",
      href: "/challenges",
      icon: <Target size={20} />,
      allowedRoles: ["admin", "user", "developer"],
    },
    {
      title: "Scoreboard",
      href: "/scoreboard",
      icon: <Trophy size={20} />,
      allowedRoles: ["admin", "co-admin", "user", "developer"],
    },
    {
      title: "Einladungen",
      href: "/invitations",
      icon: <UserPlus size={20} />,
      allowedRoles: ["admin", "co-admin", "user", "developer"],
      requiresPosition: "praktikant", // Only show for Praktikanten
    },
    {
      title: "Performance Metriken",
      href: "/performance-metrics",
      icon: <BarChart3 size={20} />,
      allowedRoles: ["admin", "developer"],
    },
    {
      title: "Nutzerverwaltung",
      href: "/user-management",
      icon: <UserCog size={20} />,
      allowedRoles: ["admin", "developer"],
    },
    {
      title: "Team Übersicht",
      href: "/team-overview",
      icon: <Users size={20} />,
      allowedRoles: ["admin", "developer"],
    },
    {
      title: "Einstellungen",
      href: "/settings",
      icon: <Settings size={20} />,
      allowedRoles: ["admin", "co-admin", "user", "developer"],
    },
    // Temporarily hidden - not deleted
    // {
    //   title: "Abonnement",
    //   href: "/subscription",
    //   icon: <CreditCard size={20} />,
    //   allowedRoles: ["admin", "developer"],
    // },
  ];

  const capitalizeFirstLetter = (string?: string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <div
      className={cn(
        "flex flex-col h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300",
        collapsed ? "w-[4.5rem]" : "w-64"
      )}
    >
      <div className="flex items-center p-4 border-b border-sidebar-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            {currentUser && (
              <div className="w-8 h-8 rounded-full bg-sidebar-accent flex items-center justify-center text-sm font-medium text-sidebar-accent-foreground">
                {currentUser.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-sidebar-foreground truncate max-w-[150px]">{currentUser?.name || "Benutzer"}</span>
              {currentUser?.position && (
                <span className="flex items-center text-xs text-sidebar-foreground/60 truncate max-w-[150px]">
                  <Briefcase size={12} className="mr-1.5 flex-shrink-0" />
                  {capitalizeFirstLetter(currentUser.position)}
                </span>
              )}
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-full h-8 w-8"
          onClick={() => setCollapsed(!collapsed)}
        >
          <ChevronLeft className={cn("transition-transform", collapsed && "rotate-180")} size={18} />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto py-6 px-3">
        <div className="mb-1 px-3">
          <span className={cn("text-xs font-semibold text-sidebar-foreground/80 uppercase", collapsed && "hidden")}>Menü</span>
        </div>
        <nav className="space-y-1">
          {navigationItems
            .filter((item) => {
              // Check role permissions
              if (currentUser && !item.allowedRoles.includes(currentUser.role)) {
                return false;
              }

              // Check position requirements (for Praktikant-specific items)
              if (item.requiresPosition && currentUser?.position !== item.requiresPosition) {
                return false;
              }

              return true;
            })
            .map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center px-3 py-2 rounded-md text-sm transition-colors",
                  location.pathname === item.href
                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground",
                  collapsed && "justify-center px-2"
                )}
              >
                <span className={cn("", collapsed ? "mx-auto" : "mr-3")}>{item.icon}</span>
                {!collapsed && <span>{item.title}</span>}
              </Link>
            ))}
        </nav>
      </div>

      {currentUser && (
        <div className="p-3 border-t border-sidebar-border">
          <Button
            variant="ghost"
            size={collapsed ? "icon" : "sm"}
            className={cn("w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground", collapsed && "justify-center")}
            onClick={handleLogout}
          >
            <LogOut size={16} className={cn(collapsed ? "" : "mr-2")} />
            {!collapsed && "Abmelden"}
          </Button>
        </div>
      )}
    </div>
  );
}
